import { ApolloError, useMutation } from '@apollo/client';
import type { AccountAllocationGridReport } from '@components/AccountAllocationGrid/types';
import { AllocationMethodologies } from '@generated/graphql';
import { toWireSubAccounts } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { HOUSEHOLD_PORTFOLIO_ERROR_CODES } from '@ovComponents/4-module/widgets/portfolio/components/householdPortfolioErrorCodes';
import { useCallback } from 'react';
import {
  type StagedAccountAllocations,
  unpackStagedAccountAllocations,
} from './accountAllocationStaging';
import {
  EDIT_ACCOUNT_ALLOCATIONS_SET_ALLOCATION_METHODOLOGY,
  EDIT_ACCOUNT_ALLOCATIONS_SET_CUSTOM_EXPECTED_PERCENTAGES,
} from './editAccountAllocations.queries';

export type CommitAccountAllocationsResult =
  | { status: 'COMMITTED'; portfolioReportId: string }
  | { status: 'FAILED'; code: string; cause: Error | undefined };

export type CommitAccountAllocations = (
  staged: StagedAccountAllocations,
  report: AccountAllocationGridReport,
) => Promise<CommitAccountAllocationsResult>;

// The step shows its own error, so the global toast would repeat it. The error
// link reads this off variables, which the typed documents don't declare; it
// is spread in because TypeScript rejects it as a literal key.
const WITHOUT_ERROR_TOAST = { skipErrorHandler: true };

const householdPortfolioErrorCode = (error: unknown) => {
  const code =
    error instanceof ApolloError
      ? error.graphQLErrors[0]?.extensions?.code
      : undefined;
  return typeof code === 'string' && HOUSEHOLD_PORTFOLIO_ERROR_CODES.has(code)
    ? code
    : 'generic';
};

export const useCommitAccountAllocations = (): CommitAccountAllocations => {
  const [setAllocationMethodology] = useMutation(
    EDIT_ACCOUNT_ALLOCATIONS_SET_ALLOCATION_METHODOLOGY,
  );
  const [setCustomExpectedPercentages] = useMutation(
    EDIT_ACCOUNT_ALLOCATIONS_SET_CUSTOM_EXPECTED_PERCENTAGES,
  );

  return useCallback(
    async (staged, report) => {
      const { portfolioReportId } = staged;
      const { matrix } = unpackStagedAccountAllocations(staged);

      try {
        if (
          report.allocationMethodology !== AllocationMethodologies.Customized
        ) {
          await setAllocationMethodology({
            variables: {
              input: {
                portfolioReportId,
                allocationMethodology: AllocationMethodologies.Customized,
              },
              ...WITHOUT_ERROR_TOAST,
            },
          });
        }
        // The whole signed matrix, not a diff: the client signed every cell.
        // The aggregate check is skipped because markets moved while it was
        // out for signature; per-account checks still run.
        await setCustomExpectedPercentages({
          variables: {
            input: {
              portfolioReportId,
              subAccounts: toWireSubAccounts(report, matrix),
              skipAggregateConservation: true,
            },
            ...WITHOUT_ERROR_TOAST,
          },
        });
        return { status: 'COMMITTED', portfolioReportId };
      } catch (error) {
        return {
          status: 'FAILED',
          code: householdPortfolioErrorCode(error),
          cause: error instanceof Error ? error : undefined,
        };
      }
    },
    [setAllocationMethodology, setCustomExpectedPercentages],
  );
};
